import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';

import type { Company } from '../../../../@types/Company';
import type { RootState } from '../../../../store/store';

export interface CommercialNameStepHandle {
  isValid: () => boolean;
  getPartialDraft: () => CommercialNameDraft;
  focusFirstInvalid: () => void;
}

type CommercialNameDraft = Pick<NonNullable<Company['brandingSettings']>, 'commercialName' | 'showLegalInfoOnBlog'>;

interface CommercialNameStepProps {
  initial: CommercialNameDraft;
  onChange?: (partial: CommercialNameDraft) => void;
  disabled?: boolean;
}

const CommercialNameStep = forwardRef<CommercialNameStepHandle, CommercialNameStepProps>(({ initial, onChange, disabled = false }, ref) => {
  const company = useSelector((state: RootState) => state.auth.company) as Company;
  const [commercialName, setCommercialName] = useState(initial.commercialName || '');
  const [showLegalInfoOnBlog, setShowLegalInfoOnBlog] = useState(initial.showLegalInfoOnBlog ?? true);
  const displayName = commercialName.trim() || company?.name || 'Votre entreprise';
  const address = [company?.details?.address, [company?.details?.postalCode, company?.details?.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');

  useImperativeHandle(ref, () => ({
    // Always valid since commercial name is optional
    isValid: () => true,
    getPartialDraft: () => ({ commercialName, showLegalInfoOnBlog }),
    focusFirstInvalid: () => {},
  }), [commercialName, showLegalInfoOnBlog]);

  return (
    <div>
      <h3 className="mb-4 text-lg font-semibold text-gray-800">Nom commercial de l'entreprise</h3>
      <div className="space-y-4">
        <div>
          <label htmlFor="commercialName" className="mb-1 block text-sm font-medium text-gray-700">
            Nom commercial (optionnel)
          </label>
          <input
            type="text"
            id="commercialName"
            value={commercialName}
            disabled={disabled}
            onChange={(e) => {
              const value = e.target.value;
              setCommercialName(value);
              if (onChange) { onChange({ commercialName: value }); }
            }}
            placeholder="Laissez vide pour utiliser le nom légal"
            className="w-full rounded-lg border border-gray-300 p-2.5 focus:border-transparent focus:ring-2 focus:ring-rgpd-blue"
          />
          <p className="mt-2 text-sm text-gray-500">
            Si renseigné, ce nom sera utilisé à la place du nom légal ({company?.name || 'votre entreprise'}) dans les newsletters, le blog, et toutes les communications destinées à vos clients.
          </p>
        </div>
        <div className="border-t border-gray-200 pt-5">
          <h4 className="mb-3 font-semibold text-gray-800">Identité affichée sur le blog</h4>
          <div className="flex items-start justify-between gap-4">
            <div>
              <label htmlFor="showLegalInfoOnBlog" className="block cursor-pointer text-sm font-medium text-gray-700">
                Afficher le nom légal et l&apos;adresse sur le blog
              </label>
              <p id="blogIdentityDescription" className="mt-1 text-sm text-gray-600">
                Désactivez cette option pour afficher uniquement le nom commercial dans les informations de votre entreprise sur le blog.
              </p>
            </div>
            <button
              type="button"
              id="showLegalInfoOnBlog"
              role="switch"
              aria-checked={showLegalInfoOnBlog}
              aria-label="Afficher le nom légal et l'adresse sur le blog"
              aria-describedby="blogIdentityDescription"
              disabled={disabled}
              onClick={() => {
                const value = !showLegalInfoOnBlog;
                setShowLegalInfoOnBlog(value);
                onChange?.({ showLegalInfoOnBlog: value });
              }}
              className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors focus-visible:ring-2 focus-visible:ring-rgpd-blue focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${showLegalInfoOnBlog ? 'bg-rgpd-blue' : 'bg-gray-500'}`}
            >
              <span aria-hidden="true" className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${showLegalInfoOnBlog ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>
          <div className="mt-4 rounded-lg bg-gray-50 p-4 text-sm text-gray-700" aria-live="polite">
            <p className="mb-2 font-medium">{showLegalInfoOnBlog ? 'Nom légal et adresse affichés' : 'Nom commercial uniquement'}</p>
            <p>{displayName}</p>
            {showLegalInfoOnBlog && (
              <>
                {company?.name !== displayName && <p>{company?.name}</p>}
                {address && <p>{address}</p>}
              </>
            )}
          </div>
          {!showLegalInfoOnBlog && !commercialName.trim() && (
            <p className="mt-2 text-sm text-gray-600">Renseignez un nom commercial ci-dessus pour remplacer le nom légal.</p>
          )}
          <p className="mt-2 text-sm text-gray-600">Le choix sera appliqué au blog après enregistrement.</p>
        </div>
      </div>
    </div>
  );
});

CommercialNameStep.displayName = 'CommercialNameStep';

export default CommercialNameStep;
